// Only bounded, explicit event parameters are sent; never form values or link text.
const events = new Set([
  "page_view",
  "view_training",
  "form_start",
  "generate_lead",
  "form_error",
  "contact_click",
  "email_copy",
  "file_download",
  "outbound_click",
  "scroll",
]);
function createAnalytics(win, enabled) {
  let lastPage = null;
  let previousPage = "";
  let scrolled = false;
  const forms = new WeakSet();
  const allowed = () =>
    enabled &&
    /^(www\.)?irse\.se$/.test(win.location.hostname) &&
    !/^\/(?:__design|forslag|blockbibliotek)(?:\/|$)/.test(
      win.location.pathname,
    ) &&
    win.irseConsent?.getChoice()?.analytics === true;
  const context = () => {
    const path = win.location.pathname;
    const location = new URL(win.location.origin + path);
    // Retain conventional campaign codes, excluding arbitrary URL parameters.
    const source = new URL(win.location.href);
    for (const key of [
      "utm_source",
      "utm_medium",
      "utm_campaign",
      "utm_id",
      "utm_content",
      "utm_term",
    ]) {
      const value = source.searchParams.get(key);
      if (value && /^[a-z0-9_-]{1,100}$/i.test(value))
        location.searchParams.set(key, value);
    }
    const parts = path.split("/").filter(Boolean);
    return {
      page_location: location.href,
      page_path: path,
      page_type:
        parts[0] === "utbildningar"
          ? parts[1]
            ? "training"
            : "training_list"
          : parts[0] === "leverantorer"
            ? parts[1]
              ? "supplier"
              : "supplier_list"
            : parts[0] === "besiktning-service"
              ? "service"
              : parts[0] === "kontakt"
                ? "contact"
                : !parts.length
                  ? "home"
                  : "other",
      content_slug: parts.at(-1) || "home",
    };
  };
  function track(name, params = {}) {
    if (!allowed() || !events.has(name)) return false;
    const safe = {
      contact_method: null,
      form_id: null,
      link_domain: null,
      link_path: null,
      percent_scrolled: null,
    };
    if (["phone", "email"].includes(params.contact_method))
      safe.contact_method = params.contact_method;
    if (params.form_id === "irse-kontakt") safe.form_id = params.form_id;
    if (
      typeof params.link_domain === "string" &&
      /^[a-z0-9.-]+$/i.test(params.link_domain)
    )
      safe.link_domain = params.link_domain.slice(0, 100);
    if (typeof params.link_path === "string")
      safe.link_path = params.link_path.split(/[?#]/)[0].slice(0, 200);
    if (params.percent_scrolled === 90) safe.percent_scrolled = 90;
    let referrer = previousPage;
    if (!referrer) {
      try {
        referrer = new URL(win.document.referrer).origin + "/";
      } catch {
        referrer = "";
      }
    }
    win.dataLayer = win.dataLayer || [];
    win.dataLayer.push({
      event: "irse_analytics",
      analytics_event: name,
      ...context(),
      page_referrer: referrer,
      ...safe,
    });
    return true;
  }
  function page() {
    const path = win.location.pathname;
    if (!allowed() || path === lastPage) return;
    track("page_view");
    if (context().page_type === "training") track("view_training");
    previousPage = win.location.origin + path;
    lastPage = path;
    scrolled = false;
  }
  function click(event) {
    const link = event.target.closest?.("a[href]");
    if (!link) return;
    const href = link.getAttribute("href") || "";
    if (/^(tel|mailto):/i.test(href)) {
      track("contact_click", {
        contact_method: /^tel:/i.test(href) ? "phone" : "email",
      });
      return;
    }
    let url;
    try {
      url = new URL(href, win.location.href);
    } catch {
      return;
    }
    if (!/^https?:$/.test(url.protocol)) return;
    const params = { link_domain: url.hostname, link_path: url.pathname };
    if (/\.(pdf|docx?|xlsx?|zip)$/i.test(url.pathname))
      track("file_download", params);
    else if (url.hostname !== win.location.hostname)
      track("outbound_click", { link_domain: url.hostname });
  }
  function focus(event) {
    const form = event.target.closest?.('form[name="irse-kontakt"]');
    if (
      !form ||
      forms.has(form) ||
      !/^(INPUT|TEXTAREA|SELECT)$/.test(event.target.tagName) ||
      event.target.name === "website"
    )
      return;
    if (track("form_start", { form_id: "irse-kontakt" })) forms.add(form);
  }
  function scroll() {
    if (scrolled || !allowed()) return;
    const height = win.document.documentElement.scrollHeight;
    if (
      height <= win.innerHeight ||
      (win.scrollY + win.innerHeight) / height < 0.9
    )
      return;
    scrolled = track("scroll", { percent_scrolled: 90 });
  }
  function consent() {
    if (!allowed()) {
      lastPage = null;
      previousPage = "";
      return;
    }
    page();
  }
  win.document.addEventListener("click", click, true);
  win.document.addEventListener("focusin", focus);
  win.addEventListener("scroll", scroll, { passive: true });
  win.addEventListener("irse:consent", consent);
  return { track, page };
}
function initializeAnalytics(win, enabled) {
  if (!win.irseAnalytics) win.irseAnalytics = createAnalytics(win, enabled);
  return win.irseAnalytics;
}
function trackEvent(name, params) {
  try {
    if (typeof window !== "undefined")
      window.irseAnalytics?.track(name, params);
  } catch {
    // Measurement must never turn a successful contact request into an error.
  }
}
module.exports = { createAnalytics, initializeAnalytics, trackEvent };
